import {
  useGetWorkspaceFoldersQuery,
  useMoveTourMutation,
} from '@carboncopyco/data';
import type { Tour } from '@carboncopyco/types';
import { ConfirmAlert, TextBase, notify } from '@carboncopyco/ui';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { MoveAssetModal } from '../../../MoveAssetModal';

type MovePlaybookModalProps = {
  tour: Tour;
  organizationId: string;
  workspaceId: string;
  onSuccess?: (tourId: Tour['id'], workspaceId: string) => void;
};

export const MoveTourWithFoldersModal = NiceModal.create(
  ({
    tour,
    organizationId,
    workspaceId,
    onSuccess,
  }: MovePlaybookModalProps) => {
    const modal = useModal();
    const [moveTour, { isLoading }] = useMoveTourMutation();
    const { data: folders } = useGetWorkspaceFoldersQuery({
      organizationId,
      workspaceId,
    });
    const tourFolderId = String(tour.folderId);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const flags = useFlags();

    return (
      <>
        <MoveAssetModal
          close={modal.remove}
          origin={{
            id: tourFolderId,
            workspaceId,
            entity: tourFolderId === '0' ? 'workspace' : 'folder',
          }}
          onSubmit={(targetFolderId, targetWorkspaceId) => {
            moveTour({
              targetWorkspaceId,
              organizationId,
              folderId: targetFolderId,
              id: tour.id,
              currentFolderId: tourFolderId,
              workspaceId: tour.workspaceId,
            })
              .unwrap()
              .then(({ id, workspaceId }) => {
                onSuccess?.(id, workspaceId);
                const folderName = folders.find(
                  (folder) => String(folder.id) === targetFolderId
                )?.name;
                notify.success(
                  `Tour successfully moved to ${targetFolderId === '0' ? 'workspace' : folderName}`
                );
                modal.remove();
              })
              .catch(() => {
                if (flags.playbook) {
                  setShowErrorModal(true);
                  return;
                }
                notify.error('Something went wrong');
                modal.remove();
              });
          }}
          isLoading={isLoading}
        />
        {showErrorModal && (
          <ConfirmAlert
            containerClass="w-[500px]"
            noSecondButton
            danger
            yesLabel="Got it"
            title="The action cannot be completed"
            show={showErrorModal}
            onConfirm={() => setShowErrorModal(false)}
            onClose={() => setShowErrorModal(false)}
          >
            <div className="flex flex-col">
              <TextBase
                color="text-ds-natural-600"
                label="You might be trying to move a Tour that is part of a Playbook."
              />
              <TextBase
                color="text-ds-natural-600"
                label="Remove it from the Playbook and try again."
              />
            </div>
          </ConfirmAlert>
        )}
      </>
    );
  }
);
